import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { inventoryCounts, type InventoryCountListItem } from '@/api/inventoryCounts';
import { orgs } from '@/api/catalogs';
import { refs } from '@/api/refs';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

export default function InventoryCountsPage() {
  const navigate = useNavigate();
  const list = useQuery({
    queryKey: ['inventory-counts'],
    queryFn: () => inventoryCounts.list({ limit: 200 }),
  });
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const orgId = orgList.data?.items[0]?.id;
  const warehousesQ = useQuery({
    queryKey: ['warehouses', orgId],
    queryFn: () => refs.warehouses(orgId!),
    enabled: !!orgId,
  });

  const whName = useMemo(() => {
    const m = new Map<string, string>();
    warehousesQ.data?.forEach((w) => m.set(w.id, `${w.code} — ${w.name}`));
    return m;
  }, [warehousesQ.data]);

  const cols = useMemo<ColumnDef<InventoryCountListItem>[]>(() => [
    { key: 'document_number', header: '№', width: 150, importBadge: true, sortValue: (r) => r.document_number ?? '', cell: (r) => r.document_number },
    {
      key: 'document_date', header: 'Дата документа', width: 170,
      sortValue: (r) => r.document_date ?? '',
      cell: (r) => r.document_date ? new Date(r.document_date).toLocaleString('uk-UA') : '',
    },
    {
      key: 'inventory_date', header: 'Дата інвентаризації', width: 160,
      sortValue: (r) => r.inventory_date ?? '',
      cell: (r) => r.inventory_date ? new Date(r.inventory_date).toLocaleDateString('uk-UA') : '',
    },
    {
      key: 'warehouse_id', header: 'Склад',
      sortValue: (r) => whName.get(r.warehouse_id) ?? '',
      cell: (r) => whName.get(r.warehouse_id) ?? '',
    },
    { key: 'state', header: 'Стан', width: 120, sortValue: (r) => r.state ?? '', cell: (r) => r.state },
    {
      key: 'posted', header: 'Проведено', width: 130, align: 'center',
      sortValue: (r) => r.posted ? 1 : 0,
      cell: (r) => (r.posted ? '✓ так' : 'ні'),
    },
  ], [whName]);

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Інвентаризація товарів</h2>
        <button className="primary" onClick={() => navigate('/inventory-counts/new')}>
          + Нова інвентаризація
        </button>
      </div>
      <DataTable<InventoryCountListItem>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        onRowDoubleClick={(r) => navigate(`/inventory-counts/${r.id}`)}
        rowMuted={(r) => r.deletion_mark ? 0.4 : undefined}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </>
  );
}
